<template>
  <z-dialog
    v-model="reactData.filterVisible"
    :title="t('z.table.filterTitle')"
    width="56%"
    @opened="onFilterDialogOpened"
  >
    <div class="z-grid-filter-panel">
      <div
        v-for="(group, groupIndex) in reactData.filterGroups"
        :key="group.id"
        class="filter-group"
      >
        <div class="filter-group__header">
          <span class="filter-group__label">
            <template v-if="groupIndex === 0">{{ t('z.table.filterGroup') }}</template>
            <template v-else>
              <z-select v-model="group.groupLogic" class="filter-group__group-logic">
                <z-option
                  v-for="opt in LOGIC_OPTIONS"
                  :key="opt.value"
                  :value="opt.value"
                  :label="t(opt.label)"
                />
              </z-select>
              <span class="filter-group__label-text">{{ t('z.table.filterGroup') }}</span>
            </template>
          </span>
          <div class="filter-group__header-actions">
            <z-select v-model="group.logic" size="small" class="filter-group__inner-logic">
              <z-option value="and" :label="t('z.table.filterGroupInnerAnd')" />
              <z-option value="or" :label="t('z.table.filterGroupInnerOr')" />
            </z-select>
            <z-button
              v-if="reactData.filterGroups.length > 1"
              link
              type="danger"
              @click="removeGroup(groupIndex)"
            >
              {{ t('z.table.removeFilterGroup') }}
            </z-button>
          </div>
        </div>

        <div
          v-for="(cond, condIndex) in group.conditions"
          :key="cond.id"
          class="filter-row"
        >
          <div class="filter-row__remove">
            <z-button
              v-if="group.conditions.length > 1"
              link
              type="danger"
              @click="removeCondition(groupIndex, condIndex)"
            >
              x
            </z-button>
          </div>
          <div class="filter-row__logic">
            <z-select
              v-if="condIndex !== 0"
              v-model="cond.logic"
              class="filter-row__logic-select"
            >
              <z-option
                v-for="opt in LOGIC_OPTIONS"
                :key="opt.value"
                :value="opt.value"
                :label="t(opt.label)"
              />
            </z-select>
          </div>
          <z-select v-model="cond.field" class="filter-row__field" size="default" @change="fieldChange(cond)">
            <z-option
              v-for="opt in internalData.titleList"
              :key="opt.field"
              :value="opt.field"
              :label="opt.title"
            />
          </z-select>
          <z-select v-model="cond.operator" class="filter-row__operator" size="default">
            <z-option
              v-for="opt in cond.operatorList"
              :key="opt.value"
              :value="opt.value"
              :label="t(opt.label)"
            />
          </z-select>
          <grid-filter-value
            v-if="!isValuelessOperator(cond.operator) && resolveFilterColumn(cond.field)"
            :ref="(el) => setValueRef(cond.id, el)"
            v-model="cond.value"
            :column="resolveFilterColumn(cond.field)!"
            class="filter-row__value"
          />
          <div v-else class="filter-row__value filter-row__value--empty" />
        </div>

        <z-button class="filter-group__add-cond" link @click="addCondition(groupIndex)">
          {{ t('z.table.addFilter') }}
        </z-button>
      </div>

      <div class="z-grid-filter-panel__footer">
        <z-button @click="clearAllFilter">{{ t('z.table.clearFilter') }}</z-button>
        <div>
          <z-button @click="addGroup">{{ t('z.table.addFilterGroup') }}</z-button>
          <z-button type="primary" @click="applyFilter">{{ t('z.table.confirmFilter') }}</z-button>
        </div>
      </div>
    </div>
  </z-dialog>
</template>

<script setup lang="ts">
import { inject, nextTick, watch, type ComponentPublicInstance } from 'vue'
import ZSelect, { ZOption } from '@zoryn/components/select'
import ZButton from '@zoryn/components/button'
import ZDialog from '@zoryn/components/dialog'
import { useLocale } from '@zoryn/hooks'
import GridFilterValue from './grid-filter-value.vue'
import { getOperator, LOGIC_OPTIONS, resolveDefaultFilterOperator, resolveFilterColumnType } from '../filter/filter-meta'
import {
  createFilterCondition,
  createFilterGroup,
  ensureDefaultFilterGroups,
  sanitizeFilterGroups,
} from '../filter/filter-group'
import {
  buildFilterPayload,
  getFirstFilterableColumn,
  isEmptyFilterValue,
  isValuelessOperator,
} from '../filter/filter-utils'
import type { DataGrid, GridFilter } from '../types'

const { t } = useLocale()
const $grid = inject('$grid') as DataGrid
const { reactData, internalData } = $grid

type FilterValueExpose = ComponentPublicInstance & { focus?: () => void }

const valueRefs = new Map<string, FilterValueExpose>()

function setValueRef(id: string, el: unknown) {
  if (el) {
    valueRefs.set(id, el as FilterValueExpose)
    return
  }
  valueRefs.delete(id)
}

function findFirstEmptyValueConditionId() {
  for (const group of reactData.filterGroups) {
    for (const cond of group.conditions) {
      if (isValuelessOperator(cond.operator)) {
        continue
      }
      if (isEmptyFilterValue(cond.value)) {
        return cond.id
      }
    }
  }
  return null
}

function onFilterDialogOpened() {
  const conditionId = findFirstEmptyValueConditionId()
  if (!conditionId) {
    return
  }
  void nextTick(() => {
    valueRefs.get(conditionId)?.focus?.()
  })
}

function resolveDefaultFilterColumn() {
  return getFirstFilterableColumn(internalData.titleList)
}

function ensureDefaultGroups() {
  const column = resolveDefaultFilterColumn()
  ensureDefaultFilterGroups(reactData.filterGroups, column)
}

watch(
  () => reactData.filterVisible,
  (visible) => {
    if (visible) {
      ensureDefaultGroups()
    }
  }
)

$grid.getFilters = () => buildFilterPayload(reactData.filterGroups)

const removeGroup = (groupIndex: number) => {
  reactData.filterGroups.splice(groupIndex, 1)
}

const removeCondition = (groupIndex: number, condIndex: number) => {
  const group = reactData.filterGroups[groupIndex]
  group.conditions.splice(condIndex, 1)
  if (!group.conditions.length) {
    removeGroup(groupIndex)
  }
}

const clearAllFilter = () => {
  reactData.filterGroups = []
  reactData.filterVisible = false
  reactData.currentPage = 1
  $grid.queryByExamplePage()
}
$grid.clearAllFilter = clearAllFilter

const addCondition = (groupIndex: number) => {
  const column = resolveDefaultFilterColumn()
  if (!column) return
  reactData.filterGroups[groupIndex].conditions.push(createFilterCondition(column))
}

const addGroup = () => {
  const column = resolveDefaultFilterColumn()
  if (!column) return
  reactData.filterGroups.push(createFilterGroup(column))
}

const applyFilter = () => {
  reactData.filterGroups = sanitizeFilterGroups(reactData.filterGroups)
  reactData.filterVisible = false
  reactData.currentPage = 1
  $grid.queryByExamplePage()
}

const fieldChange = (cond: GridFilter) => {
  const column = internalData.titleList.find((c) => c.field === cond.field)
  if (!column) return
  cond.type = resolveFilterColumnType(column.type ?? 'base')
  cond.operatorList = getOperator(cond.type)
  cond.operator = resolveDefaultFilterOperator(cond.type, column)
  cond.value = null
}

function resolveFilterColumn(field?: string) {
  if (!field) {
    return undefined
  }
  return internalData.titleList.find((col) => col.field === field)
}
</script>
